import { normalizeKycNames } from "./individualName";
import { withOwnershipMirrors, withoutInapplicablePercents } from "./editOwnershipPercentage";
export function pickClientEditable(data) {
  const x = data || {};
  return {
    customer: x.customer || {},
    uaeCompany: x.uaeCompany || {},
    ownership: normalizeOwnershipData(x.ownership || {}),
    sourceOfFundsWealth: x.sourceOfFundsWealth || {},
    pepSanctions: normalizePepSanctionsData(x.pepSanctions || {}),
    clientComment: String(x.clientComment || ""),
  };
}

export function normalizeHolderType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "entity" || raw === "company" || raw === "legal person") {
    return "Entity";
  }
  if (raw === "trust") return "Trust";
  if (raw === "foundation") return "Foundation";
  if (
    raw === "individual" ||
    raw === "person" ||
    raw === "natural person" ||
    raw === "private individual"
  ) {
    return "Individual";
  }
  return value ? String(value).trim() : "";
}

function normalizeTrusteeKind(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "company" || raw === "entity" || raw === "corporate") {
    return "company";
  }
  if (raw === "individual" || raw === "person" || raw === "natural person") {
    return "individual";
  }
  return String(value || "").trim();
}

function normalizeParty(row) {
  const item = row && typeof row === "object" ? row : {};
  return {
    ...item,
    role: String(item.role || ""),
    name: String(item.name || ""),
    trusteeKind: normalizeTrusteeKind(item.trusteeKind),
    nationality: String(item.nationality || ""),
    domicile: String(item.domicile || ""),
  };
}

function normalizeArrangement(row) {
  const item = row && typeof row === "object" ? row : {};
  const arrangementType = normalizeHolderType(
    item.arrangementType || item.type || "Trust"
  );
  return {
    ...item,
    arrangementType:
      arrangementType === "Foundation" ? "Foundation" : "Trust",
    name: String(item.name || ""),
    source: String(item.source || ""),
    sourceRefRowId: String(item.sourceRefRowId || ""),
    relatedShareholderName: String(item.relatedShareholderName || ""),
    parties: Array.isArray(item.parties)
      ? item.parties.map(normalizeParty)
      : [],
  };
}

function normalizeShareholder(row) {
  const item = row && typeof row === "object" ? row : {};
  return {
    ...item,
    name: String(item.name || ""),
    holderType: normalizeHolderType(
      item.holderType || item.type || item.partyType || item.shareholderType
    ),
    beneficialOwnerType: String(
      item.beneficialOwnerType || "Beneficial owner"
    ),
    nationality: String(item.nationality || ""),
    countryOfResidence: String(item.countryOfResidence || item.domicile || ""),
    ownershipPercent: String(
      item.ownershipPercent ?? item.ownershipPct ?? item.ownership ?? ""
    ),
    shareholderEntityType: String(item.shareholderEntityType || ""),
    trustParties: Array.isArray(item.trustParties)
      ? item.trustParties.map(normalizeParty)
      : item.trustParties,
    foundationParties: Array.isArray(item.foundationParties)
      ? item.foundationParties.map(normalizeParty)
      : item.foundationParties,
  };
}

function normalizeUbo(row) {
  const item = row && typeof row === "object" ? row : {};
  return {
    ...item,
    name: String(item.name || ""),
    holderType: normalizeHolderType(item.holderType || item.type || ""),
    nationality: String(item.nationality || ""),
    countryOfResidence: String(item.countryOfResidence || item.domicile || ""),
    basisOfControl: String(item.basisOfControl || ""),
    effectiveOwnershipPercent: String(
      item.effectiveOwnershipPercent ?? item.ownershipPercent ?? ""
    ),
    ownershipPercent: String(
      item.ownershipPercent ?? item.effectiveOwnershipPercent ?? ""
    ),
  };
}

function normalizeSignatory(row) {
  const item = row && typeof row === "object" ? row : {};
  const signatureMode = item.signatureMode === "joint" ? "joint" : "sole";
  return {
    ...item,
    name: String(item.name || ""),
    capacity: String(item.capacity || item.role || item.title || ""),
    nationality: String(item.nationality || ""),
    countryOfResidence: String(item.countryOfResidence || item.domicile || ""),
    signatureMode,
    jointWithSignatoryIds:
      signatureMode === "joint" && Array.isArray(item.jointWithSignatoryIds)
        ? item.jointWithSignatoryIds.map((id) => String(id || "")).filter(Boolean)
        : [],
  };
}

export function normalizeOwnershipData(rawOwnership) {
  const ownership = rawOwnership && typeof rawOwnership === "object"
    ? rawOwnership
    : {};
  const shareholders = Array.isArray(ownership.shareholders)
    ? ownership.shareholders
    : Array.isArray(ownership.directShareholders)
    ? ownership.directShareholders
    : [];
  const ubos = Array.isArray(ownership.ubos)
    ? ownership.ubos
    : Array.isArray(ownership.controllingPersonsNatural)
    ? ownership.controllingPersonsNatural
    : [];
  const signatories = Array.isArray(ownership.signatories)
    ? ownership.signatories
    : Array.isArray(ownership.authorisedSignatories)
    ? ownership.authorisedSignatories
    : Array.isArray(ownership.controllers)
    ? ownership.controllers
    : [];
  const legalArrangementList = Array.isArray(
    ownership.legalArrangements?.arrangements
  )
    ? ownership.legalArrangements.arrangements
    : Array.isArray(ownership.controllingPersonsLegalArrangements?.arrangements)
    ? ownership.controllingPersonsLegalArrangements.arrangements
    : [];
  const normalizedShareholders = shareholders.map(normalizeShareholder);
  const normalizedUbos = ubos.map(normalizeUbo);
  const normalizedSignatories = signatories.map(normalizeSignatory);
  const normalizedLegalArrangements = legalArrangementList.map(
    normalizeArrangement
  );

  return withoutInapplicablePercents({
    ...ownership,
    uboFlags: ownership.uboFlags || {},
    shareholders: normalizedShareholders,
    ubos: normalizedUbos,
    legalArrangements: { arrangements: normalizedLegalArrangements },
    signatories: normalizedSignatories,
  });
}

export function normalizePepSanctionsData(rawPepSanctions) {
  const pep = rawPepSanctions && typeof rawPepSanctions === "object"
    ? rawPepSanctions
    : {};
  return {
    pepDeclared:
      typeof pep.pepDeclared === "boolean" ? pep.pepDeclared : false,
    pepDetails: String(pep.pepDetails || ""),
    sanctionsDeclared:
      typeof pep.sanctionsDeclared === "boolean"
        ? pep.sanctionsDeclared
        : false,
    sanctionsDetails: String(pep.sanctionsDetails || ""),
  };
}

export function normalizeClientKycData(data) {
  const x = data || {};
  return {
    ...x,
    customer: x.customer || {},
    uaeCompany: x.uaeCompany || {},
    ownership: normalizeOwnershipData(x.ownership || {}),
    sourceOfFundsWealth: x.sourceOfFundsWealth || {},
    pepSanctions: normalizePepSanctionsData(x.pepSanctions || {}),
    clientComment: String(x.clientComment || ""),
  };
}

function isPlainObject(value) {
  return (
    !!value &&
    typeof value === "object" &&
    (value.constructor === Object || Object.getPrototypeOf(value) === null)
  );
}

export function removeUndefinedDeep(value) {
  if (value === undefined) return undefined;

  if (Array.isArray(value)) {
    return value
      .map((item) => removeUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }

  if (!isPlainObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value)
      .map(([key, item]) => [key, removeUndefinedDeep(item)])
      .filter(([, item]) => item !== undefined)
  );
}

export function toKycDateOrNull(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (seconds != null) {
      const millis =
        Number(seconds) * 1000 +
        Math.floor(Number(value.nanoseconds ?? value._nanoseconds ?? 0) / 1e6);
      const date = new Date(millis);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function withOwnershipAliases(ownership) {
  const normalized = normalizeOwnershipData(ownership);
  return normalized;
}

export function sanitizeOwnershipForSave(rawOwnership, rawCustomer) {
  const ownership = normalizeOwnershipData(rawOwnership || {});
  const customer = rawCustomer || {};
  const uboFlags = ownership?.uboFlags || {};
  const shareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : [];
  const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
  const signatories = Array.isArray(ownership?.signatories)
    ? ownership.signatories
    : [];
  const isPicOrPassiveHolding = !!uboFlags?.pctNotMeaningful;
  const isQuotedCompany = !!uboFlags?.quotedCompany;
  const isTrustAccountHolder = !!uboFlags?.accountHolderTrust;
  const isFoundationAccountHolder = !!uboFlags?.accountHolderFoundation;
  const accountHolderTrustNameKind = String(
    ownership?.accountHolderTrustNameKind || ""
  )
    .trim()
    .toLowerCase();
  const customerName = String(customer?.name || "").trim();
  const isNoneOfAbove = !!ownership?.preliminaryNoneOfAbove;
  const desktopEntityType = String(ownership?.desktopClientEntityType || "")
    .trim()
    .toLowerCase();
  const desktopHoldingKind = String(ownership?.desktopClientHoldingKind || "")
    .trim()
    .toLowerCase();
  const isDesktopStandardOwnership =
    desktopEntityType === "operating" ||
    (desktopEntityType === "holding" && desktopHoldingKind === "active");
  const hasPreliminarySelection =
    isPicOrPassiveHolding ||
    isQuotedCompany ||
    isTrustAccountHolder ||
    isFoundationAccountHolder ||
    isNoneOfAbove ||
    isDesktopStandardOwnership;

  if (!hasPreliminarySelection) {
    return withOwnershipAliases({
      ...ownership,
      shareholders: [],
      ubos: [],
      signatories: [],
    });
  }

  if (isQuotedCompany) {
    return withOwnershipAliases({
      ...ownership,
      shareholders: [],
      ubos: [],
      signatories,
    });
  }

  if (isTrustAccountHolder || isFoundationAccountHolder) {
    const targetType = isTrustAccountHolder ? "trust" : "foundation";
    const matchingShareholder =
      shareholders.find(
        (item) =>
          String(item?.holderType || "").trim().toLowerCase() === targetType
      ) || null;
    const fallbackShareholder = shareholders[0] || null;

    return withOwnershipAliases({
      ...ownership,
      shareholders:
        matchingShareholder || fallbackShareholder
          ? [
              (() => {
                const nextShareholder = {
                  ...(matchingShareholder || fallbackShareholder),
                  holderType: normalizeHolderType(targetType),
                };

                if (isTrustAccountHolder) {
                  const trustParties = Array.isArray(
                    nextShareholder?.trustParties
                  )
                    ? nextShareholder.trustParties
                    : [];
                  nextShareholder.name =
                    accountHolderTrustNameKind === "trust" && customerName
                      ? customerName
                      : nextShareholder.name || "";
                  nextShareholder.trustParties = trustParties.map((party) =>
                    String(party?.role || "") === "Trustee" &&
                    accountHolderTrustNameKind === "trustee" &&
                    customerName
                      ? {
                          ...(party || {}),
                          name: customerName,
                          trusteeKind: "entity",
                        }
                      : party
                  );
                }

                return nextShareholder;
              })(),
            ]
          : [],
      ubos: [],
      signatories,
    });
  }

  if (isPicOrPassiveHolding) {
    return withOwnershipAliases({
      ...ownership,
      shareholders: shareholders.map((item) => ({
        ...(item || {}),
        ownershipPercent: "",
      })),
      ubos,
      signatories,
    });
  }

  return withOwnershipAliases({
    ...ownership,
    shareholders,
    ubos,
    signatories,
  });
}

export function buildClientKycSaveData(form, mode = "full") {
  const editableData = pickClientEditable(normalizeKycNames(form, mode));
  return {
    ...editableData,
    ownership: sanitizeOwnershipForSave(
      editableData.ownership,
      editableData.customer
    ),
  };
}
